import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Stack, Group, Button, Radio, TextInput, MultiSelect, Select, Checkbox, Text, Progress, Card } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';

interface SetupWizardProps {
  opened: boolean;
  onClose: () => void;
  onCreateDashboard: (config: DashboardConfig) => Promise<void>;
  goalOptions: Array<{ value: string; label: string }>;
  campaignOptions: Array<{ value: string; label: string }>;
  revenueGoalOptions: Array<{ value: string; label: string }>;
  fetchGoalOptions: (campaignIds?: string[]) => void;
  accountName?: string;
  isLoading?: boolean;
}

export interface DashboardConfig {
  dashboard_type: 'business' | 'marketing';
  dashboard_name: string;
  goal_id: string;
  revenue_goal_id: string | null;
  campaign_ids: string[];
  set_as_default: boolean;
}

const getDefaultName = (type: 'business' | 'marketing', account: string) => {
  const suffix = account ? ` — ${account}` : '';
  return type === 'business' ? `Business Dashboard${suffix}` : `Marketing Dashboard${suffix}`;
};

export function SetupWizard({
  opened,
  onClose,
  onCreateDashboard,
  goalOptions,
  campaignOptions,
  revenueGoalOptions,
  fetchGoalOptions,
  accountName = '',
  isLoading = false,
}: SetupWizardProps) {
  const [step, setStep] = useState(0);
  const [config, setConfig] = useState<DashboardConfig>({
    dashboard_type: 'business',
    dashboard_name: getDefaultName('business', accountName),
    goal_id: '',
    revenue_goal_id: null,
    campaign_ids: [],
    set_as_default: true,
  });
  const [error, setError] = useState('');

  const normalizedGoalOptions = useMemo(() => {
    return goalOptions
      .map((option) => {
        const value = option?.value != null ? String(option.value).trim() : '';
        if (!value) return null;
        const label = option?.label != null && String(option.label).trim()
          ? String(option.label)
          : value;
        return { value, label };
      })
      .filter((option): option is { value: string; label: string } => option !== null);
  }, [goalOptions]);
  const normalizedRevenueGoalOptions = useMemo(() => {
    return revenueGoalOptions
      .map((option) => {
        const value = option?.value != null ? String(option.value).trim() : '';
        if (!value) return null;
        const label = option?.label != null && String(option.label).trim()
          ? String(option.label)
          : value;
        return { value, label };
      })
      .filter((option): option is { value: string; label: string } => option !== null);
  }, [revenueGoalOptions]);
  const normalizedGoalId = config.goal_id ? String(config.goal_id).trim() : '';
  const normalizedRevenueGoalId = config.revenue_goal_id ? String(config.revenue_goal_id).trim() : null;

  // When campaigns change, re-fetch goals filtered by those campaigns —
  // mirrors the settings panel behaviour so only goals shared by the
  // selected campaigns appear in the Primary Goal step.
  const campaignKey = config.campaign_ids.join(',');
  useEffect(() => {
    if (config.campaign_ids.length > 0) {
      fetchGoalOptions(config.campaign_ids);
    } else {
      fetchGoalOptions();
    }
    // Clear any previously selected goal — the narrowed list may not contain it.
    setConfig((prev) => (prev.goal_id || prev.revenue_goal_id ? { ...prev, goal_id: '', revenue_goal_id: null } : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignKey]);

  const handleNext = () => {
    setError('');

    // Validation
    if (step === 0 && !config.dashboard_type) {
      setError('Select a dashboard type');
      return;
    }
    if (step === 2 && config.campaign_ids.length === 0) {
      setError('Select at least one campaign');
      return;
    }
    if (step === 3 && !normalizedGoalId) {
      setError('Select a goal');
      return;
    }

    if (step < 5) {
      setStep(step + 1);
    }
  };

  const handleCreate = async () => {
    setError('');
    
    if (!config.dashboard_name.trim()) {
      setError('Dashboard name is required');
      return;
    }
    if (!normalizedGoalId) {
      setError('Goal is required');
      return;
    }
    if (config.campaign_ids.length === 0) {
      setError('Select at least one campaign');
      return;
    }

    try {
      await onCreateDashboard(config);
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create dashboard');
    }
  };

  const handleClose = () => {
    setStep(0);
    setConfig({
      dashboard_type: 'business',
      dashboard_name: getDefaultName('business', accountName),
      goal_id: '',
      revenue_goal_id: null,
      campaign_ids: [],
      set_as_default: true,
    });
    setError('');
    onClose();
  };

  const updateConfig = (updates: Partial<DashboardConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  };

  const updateType = (type: 'business' | 'marketing') => {
    updateConfig({
      dashboard_type: type,
      dashboard_name: getDefaultName(type, accountName),
    });
  };

  const progressPercent = ((step + 1) / 6) * 100;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Create Dashboard — Step ${step + 1} of 6`}
      centered
      size="lg"
      closeOnClickOutside={false}
    >
      <Stack spacing="xl">
        <Progress value={progressPercent} size="sm" />

        {error && (
          <Card bg="#FEF2F2" p="md" style={{ border: '1px solid #FECACA' }}>
            <Group spacing="xs">
              <IconAlertCircle size={18} color="rgb(239, 68, 68)" />
              <Text size="sm" color="rgb(239, 68, 68)" style={{ flex: 1 }}>
                {error}
              </Text>
            </Group>
          </Card>
        )}

        {/* Step 0: Dashboard Type */}
        {step === 0 && (
          <Stack spacing="md">
            <Text fw={500}>Choose Dashboard Type</Text>
            <Radio.Group
              value={config.dashboard_type}
              onChange={(value) => updateType(value as 'business' | 'marketing')}
            >
              <Stack spacing="md">
                <Card
                  p="md"
                  style={{
                    border: config.dashboard_type === 'business' ? '2px solid #7C3AED' : '1px solid #E8E8E8',
                    cursor: 'pointer',
                  }}
                  onClick={() => updateType('business')}
                >
                  <Radio value="business" label="Business Dashboard" />
                  <Text size="sm" color="dimmed" mt="xs">
                    See if your marketing is working and where your money is going
                  </Text>
                </Card>

                <Card
                  p="md"
                  style={{
                    border: config.dashboard_type === 'marketing' ? '2px solid #7C3AED' : '1px solid #E8E8E8',
                    cursor: 'pointer',
                  }}
                  onClick={() => updateType('marketing')}
                >
                  <Radio value="marketing" label="Marketing Dashboard" />
                  <Text size="sm" color="dimmed" mt="xs">
                    Find where the system is breaking and what to fix
                  </Text>
                </Card>
              </Stack>
            </Radio.Group>
          </Stack>
        )}

        {/* Step 1: Dashboard Name */}
        {step === 1 && (
          <Stack spacing="md">
            <Text fw={500}>Dashboard Name</Text>
            <TextInput
              placeholder="Enter dashboard name"
              value={config.dashboard_name}
              onChange={(e) => updateConfig({ dashboard_name: e.currentTarget.value })}
              maxLength={60}
            />
          </Stack>
        )}

        {/* Step 2: Select Campaigns */}
        {step === 2 && (
          <Stack spacing="md">
            <Text fw={500}>Select Campaigns</Text>
            <MultiSelect
              placeholder="Select campaigns"
              data={campaignOptions}
              value={config.campaign_ids}
              onChange={(value) => updateConfig({ campaign_ids: value })}
              searchable
              withinPortal
              dropdownPosition="bottom"
            />
            {config.campaign_ids.length > 0 && (
              <Text size="xs" color="dimmed" style={{ fontStyle: 'italic' }}>
                Some campaigns use different step sequences. Funnel views may be limited to shared steps.
              </Text>
            )}
            <Text size="xs" color="dimmed" style={{ fontStyle: 'italic' }}>
              Benchmarks must be set in Campaign Reports to enable performance insights.
            </Text>
          </Stack>
        )}

        {/* Step 3: Select Primary Goal (filtered by selected campaigns) */}
        {step === 3 && (
          <Stack spacing="md">
            <Text fw={500}>Select Goal</Text>
            <Text size="sm" color="dimmed">The main result you want to track</Text>
            <Select
              placeholder="Select primary goal"
              data={normalizedGoalOptions}
              value={normalizedGoalId || null}
              onChange={(value) => updateConfig({ goal_id: value ? String(value).trim() : '' })}
              searchable
              withinPortal
              dropdownPosition="bottom"
            />
            {normalizedGoalOptions.length === 0 && (
              <Card bg="#FEF2F2" p="md">
                <Text size="sm" color="rgb(239, 68, 68)">
                  No goals found for the selected campaigns. Go back and pick different campaigns.
                </Text>
              </Card>
            )}
          </Stack>
        )}

        {/* Step 4: Revenue Goal (Optional) */}
        {step === 4 && (
          <Stack spacing="md">
            <Text fw={500}>Select Revenue Goal</Text>
            <Text size="sm" color="dimmed">The event that represents money coming in (e.g. Purchase, Sale Closed)</Text>
            <Select
              placeholder="Select revenue goal (optional)"
              data={normalizedRevenueGoalOptions}
              value={normalizedRevenueGoalId}
              onChange={(value) => updateConfig({ revenue_goal_id: value ? String(value).trim() : null })}
              searchable
              clearable
              withinPortal
              dropdownPosition="bottom"
            />
            <Button
              variant="subtle"
              color="gray"
              compact
              onClick={() => {
                updateConfig({ revenue_goal_id: null });
                setStep(5);
              }}
              style={{ alignSelf: 'flex-start' }}
            >
              Skip for now
            </Button>
          </Stack>
        )}

        {/* Step 5: Review + Create */}
        {step === 5 && (
          <Stack spacing="md">
            <Text fw={500}>Review Your Configuration</Text>
            <Card bg="#FAFAFA" p="md">
              <Stack spacing="sm">
                <Group position="apart">
                  <Text size="sm" color="dimmed">Dashboard Type</Text>
                  <Text size="sm" fw={500}>
                    {config.dashboard_type === 'business' ? 'Business Dashboard' : 'Marketing Dashboard'}
                  </Text>
                </Group>
                <Group position="apart">
                  <Text size="sm" color="dimmed">Name</Text>
                  <Text size="sm" fw={500}>{config.dashboard_name}</Text>
                </Group>
                <Group position="apart">
                  <Text size="sm" color="dimmed">Goal</Text>
                  <Text size="sm" fw={500}>
                    {normalizedGoalOptions.find((g) => g.value === normalizedGoalId)?.label || 'Not set'}
                  </Text>
                </Group>
                <Group position="apart">
                  <Text size="sm" color="dimmed">Revenue Goal</Text>
                  <Text size="sm" fw={500}>
                    {normalizedRevenueGoalId
                      ? normalizedRevenueGoalOptions.find((g) => g.value === normalizedRevenueGoalId)?.label
                      : 'Not set'}
                  </Text>
                </Group>
                <Group position="apart">
                  <Text size="sm" color="dimmed">Campaigns</Text>
                  <Text size="sm" fw={500}>
                    {config.campaign_ids.length} selected
                  </Text>
                </Group>
              </Stack>
            </Card>

            <Checkbox
              label="Set as default dashboard"
              checked={config.set_as_default}
              onChange={(e) => updateConfig({ set_as_default: e.currentTarget.checked })}
            />
          </Stack>
        )}

        {/* Navigation */}
        <Group position="right" spacing="sm">
          <Button variant="default" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          {step > 0 && (
            <Button variant="default" onClick={() => setStep(step - 1)} disabled={isLoading}>
              Back
            </Button>
          )}
          {step < 5 && (
            <Button onClick={handleNext} disabled={isLoading}>
              Next
            </Button>
          )}
          {step === 5 && (
            <Button onClick={handleCreate} loading={isLoading} color="green">
              Create Dashboard
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
